import { ServiceAccountJsonUpload } from '@/components/admin/ServiceAccountJsonUpload';
import { Button } from '@/components/ui/button';
import { Field } from '@/components/ui/field';
import { Input } from '@/components/ui/input';
import { ApiError, api } from '@/lib/api';
import { type CreateDataSourceRequest, CreateDataSourceRequestSchema } from '@innermate/shared';
import { CheckCircle2, Loader2, XCircle } from 'lucide-react';
import { useState } from 'react';

interface BigQueryFormProps {
  submitLabel: string;
  submitting?: boolean;
  onSubmit: (body: CreateDataSourceRequest) => void;
  onCancel?: () => void;
}

type TestStatus = 'idle' | 'testing' | 'ok' | 'fail';

export function BigQueryForm({ submitLabel, submitting, onSubmit, onCancel }: BigQueryFormProps) {
  const [name, setName] = useState('');
  const [credentials, setCredentials] = useState(''); // raw service-account JSON
  const [clientEmail, setClientEmail] = useState('');
  const [billingProject, setBillingProject] = useState(''); // SA project (job billing)
  const [dataProject, setDataProject] = useState(''); // explored project (database)
  const [location, setLocation] = useState('');
  const [datasets, setDatasets] = useState('');
  const [description, setDescription] = useState('');
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [testStatus, setTestStatus] = useState<TestStatus>('idle');
  const [testMessage, setTestMessage] = useState<string | null>(null);

  const resetTest = () => {
    if (testStatus !== 'idle') {
      setTestStatus('idle');
      setTestMessage(null);
    }
  };

  const buildPayload = (): CreateDataSourceRequest | null => {
    const draft = {
      name,
      kind: 'bigquery' as const,
      host: 'bigquery',
      port: 443,
      database: dataProject,
      username: clientEmail || 'bigquery',
      password: credentials,
      sslMode: 'disable' as const,
      schemaFilter: datasets
        .split(/[\n,]/)
        .map((s) => s.trim())
        .filter((s) => s.length > 0),
      extraConfig: {
        projectId: billingProject,
        ...(location.trim() ? { location: location.trim() } : {}),
      },
      description: description.length === 0 ? null : description,
    };
    const parsed = CreateDataSourceRequestSchema.safeParse(draft);
    if (!parsed.success) {
      const next: Record<string, string> = {};
      for (const issue of parsed.error.issues) {
        const k = String(issue.path[0] ?? '_');
        if (!next[k]) next[k] = issue.message;
      }
      setErrors(next);
      return null;
    }
    if (!credentials) {
      setErrors({ password: '서비스계정 JSON 을 업로드하세요.' });
      return null;
    }
    setErrors({});
    return parsed.data;
  };

  const onTest = async () => {
    const payload = buildPayload();
    if (!payload) return;
    setTestStatus('testing');
    setTestMessage(null);
    try {
      const res = await api.dataSourceTestConnection(payload);
      if (res.ok) {
        setTestStatus('ok');
        setTestMessage(`연결 성공 (${res.latencyMs}ms)`);
      } else {
        setTestStatus('fail');
        setTestMessage(res.error ?? '알 수 없는 오류');
      }
    } catch (err) {
      setTestStatus('fail');
      setTestMessage(err instanceof ApiError ? err.message : (err as Error).message);
    }
  };

  const submit = (e: React.FormEvent) => {
    e.preventDefault();
    if (testStatus !== 'ok') return;
    const payload = buildPayload();
    if (!payload) return;
    onSubmit(payload);
  };

  return (
    <form onSubmit={submit} className="space-y-5">
      <Field label="이름" htmlFor="bq-name" error={errors.name} hint="식별용. 공백 가능.">
        <Input
          id="bq-name"
          value={name}
          onChange={(e) => {
            setName(e.target.value);
            resetTest();
          }}
          placeholder="예: 마케팅 BigQuery"
        />
      </Field>

      <ServiceAccountJsonUpload
        id="bq-file"
        label="서비스계정 키 (JSON)"
        hint="GCP 서비스계정 키 JSON 파일을 업로드하세요. 암호화되어 저장됩니다."
        error={errors.password}
        onLoaded={(json, parsed) => {
          setCredentials(json);
          setClientEmail(parsed.client_email);
          setBillingProject(parsed.project_id);
          setDataProject((prev) => prev || parsed.project_id);
          resetTest();
        }}
        onError={() => setCredentials('')}
        summary={
          credentials ? (
            <p className="mt-1 text-xs text-success">
              ✓ {billingProject} · {clientEmail}
            </p>
          ) : null
        }
      />

      <div className="grid grid-cols-2 gap-4">
        <Field
          label="데이터 프로젝트 ID"
          htmlFor="bq-data-project"
          error={errors.database}
          hint="탐색할 데이터가 있는 프로젝트. 공용 데이터셋이면 그 프로젝트 ID (예: bigquery-public-data)."
        >
          <Input
            id="bq-data-project"
            value={dataProject}
            onChange={(e) => {
              setDataProject(e.target.value);
              resetTest();
            }}
            placeholder="예: my-project"
          />
        </Field>
        <Field
          label="위치 (location, 선택)"
          htmlFor="bq-location"
          hint="지역 데이터셋이면 지정 (예: US, asia-northeast3). 비우면 자동."
        >
          <Input
            id="bq-location"
            value={location}
            onChange={(e) => {
              setLocation(e.target.value);
              resetTest();
            }}
            placeholder="예: US"
          />
        </Field>
      </div>

      <Field
        label="데이터셋 필터 (선택)"
        htmlFor="bq-datasets"
        error={errors.schemaFilter}
        hint="동기화할 dataset ID 를 쉼표/줄바꿈으로 구분. 비우면 데이터 프로젝트의 모든 데이터셋."
      >
        <textarea
          id="bq-datasets"
          value={datasets}
          onChange={(e) => {
            setDatasets(e.target.value);
            resetTest();
          }}
          rows={2}
          className="w-full rounded-md border border-border bg-surface px-3 py-2 text-sm text-foreground focus-visible:outline-none"
          placeholder="예: samples, analytics_123"
        />
      </Field>

      <Field
        label="설명 (관리자용 메모)"
        htmlFor="bq-desc"
        error={errors.description}
        hint="LLM 에 전달되지 않음. 테이블/컬럼별 설명은 등록 후 편집 가능."
      >
        <textarea
          id="bq-desc"
          value={description}
          onChange={(e) => setDescription(e.target.value)}
          rows={3}
          className="w-full rounded-md border border-border bg-surface px-3 py-2 text-sm text-foreground focus-visible:outline-none"
          placeholder="이 BigQuery 데이터소스의 용도를 적어주세요."
        />
      </Field>

      <div className="rounded-xl border border-border bg-surface-muted/30 p-4">
        <p className="mb-2 text-xs text-foreground-muted">
          연결을 먼저 테스트 한 뒤 저장하세요. 저장 시 데이터셋/테이블 구조가 백그라운드에서
          동기화됩니다 (통계는 비용 절감을 위해 수동 트리거 시에만 수집).
        </p>
        <div className="flex items-center gap-2">
          <Button
            type="button"
            variant="secondary"
            onClick={onTest}
            disabled={testStatus === 'testing'}
          >
            {testStatus === 'testing' ? <Loader2 className="size-4 animate-spin" /> : null}
            연결 테스트
          </Button>
          {testStatus === 'ok' ? (
            <span className="inline-flex items-center gap-1 text-xs text-success">
              <CheckCircle2 className="size-3.5" />
              {testMessage}
            </span>
          ) : testStatus === 'fail' ? (
            <span className="inline-flex items-center gap-1 text-xs text-destructive">
              <XCircle className="size-3.5" />
              {testMessage}
            </span>
          ) : null}
        </div>
      </div>

      <div className="flex justify-end gap-2 pt-2">
        {onCancel ? (
          <Button type="button" variant="ghost" onClick={onCancel}>
            취소
          </Button>
        ) : null}
        <Button type="submit" disabled={submitting || testStatus !== 'ok'}>
          {submitting ? <Loader2 className="size-4 animate-spin" /> : null}
          {submitLabel}
        </Button>
      </div>
    </form>
  );
}
